import { IFlightItem } from "../FlightRow/FlightRow";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

import DestinationFlightRow from "../DestinationFlightRow/DestinationFlightRow";

interface IIspirationTravelFlightsProps{
    destination: string,
    language: string,
    partnerShipActive: boolean
}

const IspirationTravelFlights: React.FC<IIspirationTravelFlightsProps> = ({ destination, language, partnerShipActive }: IIspirationTravelFlightsProps) => {

    const [flightList, setFlightList] = useState<IFlightItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [apiError, setApiError] = useState<string | null>(null);
    const { t, i18n } = useTranslation();
    const lang = language || i18n.language;
    
    const currentFlights = useMemo(() => {
        return flightList?.slice(0, 6)
    }, [flightList, lang])

    const destinationMapping: { [key: string]: string } = {
        'Hanoi': 'HAN',
        'Abu Dhabi': 'AUH',
        'Roma': 'FCO',
        'Milano': 'MXP',
        'Londra': 'LHR',
        'Parigi': 'CDG',
        'New York': 'JFK',
        'Tokyo': 'NRT',
        'Dubai': 'DXB'
    };

    const getFlightsByDestination = async () => {
        try {
            setIsLoading(true);
            setApiError(null);
            
            const mappedDestination = destinationMapping[destination] || destination;
            
            const apiVariants: Array<Record<string, string>> = [
                {
                    airportReferenceIata: process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE || '',
                    loadingType: 'P',
                    destinationIataCode: mappedDestination
                },
                {
                    airportReferenceIata: process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE || '',
                    loadingType: 'P',
                    airportDestinationDescription: destination
                },
                {
                    airportReferenceIata: process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE || '',
                    loadingType: 'D',
                    airportDestinationDescription: destination
                }
            ];

            if (mappedDestination !== destination) {
                apiVariants.splice(2, 0, {
                    airportReferenceIata: process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE || '',
                    loadingType: 'P',
                    airportDestinationDescription: mappedDestination
                });
            }

            for (let i = 0; i < apiVariants.length; i++) {
                const params = new URLSearchParams(apiVariants[i]);
                const fullUrl = `${process.env.NEXT_PUBLIC_FLIGHTS_BASE_PATH}/${lang}/scheduled/flights/lists?${params.toString()}`;
                
                try {
                    const response = await fetch(fullUrl, {
                        headers: {
                            'keyId': process.env.NEXT_PUBLIC_GATEWAY_KEY as string
                        }
                    });

                    if (!response.ok) {
                        continue;
                    }

                    const result = await response.json();
                    
                    if (result.data && Array.isArray(result.data) && result.data.length > 0) {
                        setFlightList(result.data);
                        setIsLoading(false);
                        return;
                    }
                } catch (error) {
                    continue;
                }
            }

            setFlightList([]);
            setApiError(`Nessun volo diretto trovato per ${destination}`);
            
        } catch (error) {
            setApiError('Errore nel caricamento dei voli');
            setFlightList([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!destination) {
            setFlightList([]);
            setIsLoading(false);
            return;
        }

        getFlightsByDestination();
    }, [destination]);

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="w-full flex justify-center py-12">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="text-gray-600">Ricerca voli per {destination}...</p>
                    </div>
                </div>
            );
        }

        if (currentFlights?.length > 0) {
            return (
                <>
                    <div className="tableHeader w-full flex justify-center">
                        <div className="w-2/6 desk:w-1/6 desk:pl-12 text-lg font-bold text-lightblue">{t('destinations.flight')}</div>
                        <div className="hidden desk:block w-1/6 text-lg font-bold text-lightblue text-center">{t('destinations.airline')}</div>
                        <div className="w-2/6 desk:w-2/6 text-lg font-bold text-lightblue text-center">{t('destinations.activity')}</div>
                        <div className="w-1/6 text-lg font-bold text-lightblue text-center">{t('destinations.frequency')}</div>
                        <div className="hidden desk:block desk:w-1/6 text-lg font-bold text-lightblue text-center"></div>
                    </div>
                    
                    {currentFlights.map((flightItem, loopIndex) => (
                        <DestinationFlightRow 
                            actionColumn={partnerShipActive} 
                            actionLabel={t('flights.buyTickets') || 'Acquista biglietti'} 
                            actionLink={`/${lang}/voli/prenota-il-tuo-volo`} 
                            key={flightItem.flightId} 
                            flightItem={flightItem} 
                        />
                    ))}
                </>
            );
        }

        return (
            <div className="w-full flex justify-center py-12">
                <div className="max-w-md text-center space-y-6">
                    <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                    </div>
                    
                    <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                            Nessun volo diretto per {destination}
                        </h3>
                        <p className="text-gray-600">
                            {apiError || "Al momento non sono disponibili voli diretti per questa destinazione."}
                        </p>
                    </div>
                    
                    <div className="space-y-3">
                        <p className="text-sm text-gray-500">
                            Prova a cercare voli con scalo o contatta il nostro servizio clienti per maggiori informazioni.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button 
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                onClick={() => window.open(`/${lang}/voli/prenota-il-tuo-volo`, '_blank')}
                            >
                                Cerca tutti i voli
                            </button>
                            <button 
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                onClick={() => window.open('/contatti', '_blank')}
                            >
                                Contattaci
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={currentFlights?.length ? "px-5 tab:px-14 desk:px-32 py-12" : "px-5 tab:px-14 desk:px-32"}>
            <h1 className="font-semibold font-secondary text-3xl mb-24 uppercase ml-[45px]">
                {t('travel_inspiration.findYourFlightTo')} {destination}
            </h1>
            <div className="flex flex-col gap-8 w-full">
                {renderContent()}
            </div>
        </div>
    )
}

export default IspirationTravelFlights;